import { Stack } from "@mui/material"
import { useField } from "formik"
import { LocalizedStrict } from "../../../../../../modules/localization/components/AppLocalized"
import { FormikCurrencyField } from "../../../../../../modules/merchant/admin/products/components/FormikCurrencyField"
import { StandardButton } from "../../../../buttons/StandardButton"
import { FormikTextField } from "../../../../forms/Formik"
import { ProductWishFormValues, ProductWishReadOnlyField } from "./ProductWishForm"
import { sanitizeAndLimitInput } from "../../form/helpers"
import { INVALID_INPUT_KEY } from "../../../../../../modules/nonAdminUser/reports/settings/wallets/refill/components/WalletsRefillManual"
import { MAX_NAME_LENGTH, MAX_PRICE_VALUE } from "../../../../../../constants/inputValidationDefaults"

type ProductWishFormContentProps = Readonly<{
    isShowPriceField: boolean
    readOnlyFields?: ReadonlyArray<ProductWishReadOnlyField>
}>

export function ProductWishFormContent({ isShowPriceField, readOnlyFields }: ProductWishFormContentProps) {
    const [, metaPrice] = useField<ProductWishFormValues["price"]>("price")
    const isNameReadOnly = readOnlyFields?.includes("name") ?? false
    const isQuantityReadOnly = readOnlyFields?.includes("quantity") ?? false

    return (
        <Stack gap="1.5rem" alignItems="flex-start">
            <LocalizedStrict
                id="create-order-wish-dialog-product-name-field"
                attrs={{ label: true, placeholder: true }}
            >
                <FormikTextField<ProductWishFormValues>
                    name="name"
                    label="Product name *"
                    placeholder="Enter product name:"
                    fullWidth
                    disabled={isNameReadOnly}
                    inputProps={{ maxLength: MAX_NAME_LENGTH }}
                />
            </LocalizedStrict>
            <LocalizedStrict
                id="create-order-wish-dialog-product-items-field"
                attrs={{ label: true, placeholder: true }}
            >
                <FormikTextField<ProductWishFormValues>
                    label="Product items *"
                    placeholder="Enter items:"
                    name="quantity"
                    type="number"
                    disabled={isQuantityReadOnly}
                    inputProps={{
                        inputMode: "numeric",
                        onInput: (e: React.FormEvent<HTMLInputElement>) => {
                            const sanitized = sanitizeAndLimitInput(e.currentTarget.value, 6).replace(/^0+/, "")
                            if (e.currentTarget.value !== sanitized) {
                                e.currentTarget.value = sanitized
                            }
                        },
                        onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
                            if (INVALID_INPUT_KEY.includes(e.key)) {
                                e.preventDefault()
                            }
                        },
                    }}
                />
            </LocalizedStrict>
            {isShowPriceField && (
                <Stack>
                    <LocalizedStrict
                        id="create-order-wish-dialog-product-price-field"
                        attrs={{ label: true, placeholder: true }}
                    >
                        <FormikCurrencyField
                            name="price"
                            label="Product price *"
                            placeholder="Enter price (DKK):"
                            errorMessage={metaPrice.error}
                            fullWidth={false}
                            inputTextAlign="left"
                            containerDirection="column"
                            gap={0.25}
                            isAllowed={({ floatValue }) => floatValue === undefined || floatValue <= MAX_PRICE_VALUE}
                        />
                    </LocalizedStrict>
                </Stack>
            )}
            <Stack flexDirection="row" justifyContent="center" width="100%">
                <LocalizedStrict id="create-order-wish-dialog-submit-button">
                    <StandardButton fullWidth={false} type="submit">
                        Confirm
                    </StandardButton>
                </LocalizedStrict>
            </Stack>
        </Stack>
    )
}
